import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  ClosingFormModal,
  ClosingViewModal,
} from 'components/modals/modules/closing-module/ClosingTransactionModal';
import type {
  ClosingFormData,
  ClosingRecord,
  MappingOption,
  ClosingGLFormData,
} from 'components/modals/modules/closing-module/ClosingTransactionModal';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useClosings, useCreateClosing, useUpdateClosing, useSubmitClosing, useDeleteClosing, useLoanMappings } from 'hooks/api';
import { closingService } from 'api/services/closingService';
import { loanMappingService } from 'api/services/loanMappingService';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// EMPTY FORM DATA
// ============================================================================

const emptyFormData: ClosingFormData = {
  mappingId: '',
  mappingRef: '',
  customerName: '',
  customerId: '',
  loanAccountNumber: '',
  approvedAmount: 0,
  addonOutstandingPrincipal: '',
  addonOutstandingInterest: '',
  glEntries: [],
};

// ============================================================================
// MAIN CLOSING TRANSACTION SCREEN
// ============================================================================

const ClosingTransactionScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useClosings();
  const { createClosing, loading: createLoading } = useCreateClosing();
  const { submitClosing, loading: submitLoading } = useSubmitClosing();
  const { updateClosing, loading: updateLoading } = useUpdateClosing();
  const { deleteClosing, loading: deleteLoading } = useDeleteClosing();
  const { data: loanMappings } = useLoanMappings();
  // Data from API
  const closings = (apiData as unknown as ClosingRecord[]) || [];

  // Transform loan mappings to MappingOption format
  const mappingOptions: MappingOption[] = useMemo(() =>
    loanMappings.map(m => ({
      id: String(m.id),
      mappingRef: String(m.id),
      customerName: m.customerName,
      customerId: m.customerId,
      loanAccountNumber: '',
      approvedAmount: m.approvedAmount,
      outstandingPrincipal: 0,
      outstandingInterest: 0,
      gls: [],
    })),
    [loanMappings]
  );
  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<ClosingRecord[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<ClosingRecord | null>(null);
  const [formData, setFormData] = useState<ClosingFormData>(emptyFormData);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() =>
    closings.filter((c) => {
      const matchesSearch = !searchQuery ||
        (c.closingRef ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.mappingRef ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.customerId ?? '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === '' || c.statusCode === statusFilter;

      return matchesSearch && matchesStatus;
    }),
    [closings, searchQuery, statusFilter]
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormData({ ...emptyFormData, glEntries: [] });
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(async (record: ClosingRecord) => {
    try {
      const [closingResponse, mappingResponse] = await Promise.all([
        closingService.getById(record.id),
        loanMappingService.getById(record.mappingId),
      ]);
      const detail = closingResponse.data;
      const mappingDetail = mappingResponse.data;

      // Build a lookup of saved GL entries (previously selected)
      const savedGLs = new Map(
        (detail?.glEntries ?? []).map((gl) => [gl.mappedGlId || String(gl.id), gl])
      );

      // Get all OPEN GLs from the mapping, merging with saved data
      const allGLs = (mappingDetail?.mappedGls ?? [])
        .filter((gl) => gl.statusCode === 'OPEN')
        .map((gl) => {
          const saved = savedGLs.get(String(gl.id));
          return {
            mappedGlId: String(gl.id),
            glNumber: gl.glNumber ?? '',
            customerName: gl.glCustomerName ?? '',
            netWeight: gl.netWeight,
            allocationRatio: gl.allocationRatio,
            selected: !!saved,
            outstandingPrincipal: saved ? String(saved.outstandingPrincipal) : '',
            outstandingInterest: saved ? String(saved.outstandingInterest) : '',
            tokenLossCharge: saved && saved.tokenLossCharge != null ? String(saved.tokenLossCharge) : '',
          };
        });

      setFormMode('edit');
      setSelectedRecord(record);
      setFormData({
        mappingId: record.mappingId,
        mappingRef: record.mappingRef ?? '',
        customerName: record.customerName,
        customerId: record.customerId,
        loanAccountNumber: record.loanAccountNumber,
        approvedAmount: detail?.approvedAmount ?? mappingDetail?.approvedAmount ?? 0,
        addonOutstandingPrincipal: String(record.addonOutstandingPrincipal),
        addonOutstandingInterest: String(record.addonOutstandingInterest),
        glEntries: allGLs,
      });
      setFormModalOpen(true);
    } catch {
      showMessage('Failed to load closing details for editing', 'error');
    }
  }, [showMessage]);

  const handleView = useCallback((record: ClosingRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(() => {
    if (!formData.mappingId) { showMessage('Please search for a loan mapping first', 'error'); return; }
    if (!formData.addonOutstandingPrincipal || parseFloat(formData.addonOutstandingPrincipal) <= 0) { showMessage('Outstanding Principal is required', 'error'); return; }
    const oiValue = parseFloat(formData.addonOutstandingInterest) || 0;
    if (oiValue < 0) { showMessage('Outstanding Interest cannot be negative', 'error'); return; }

    const selectedGLs = formData.glEntries.filter((gl) => gl.selected);
    if (selectedGLs.length === 0) { showMessage('Select at least one Gold Loan', 'error'); return; }

    const hasEmptyGL = selectedGLs.some((gl) =>
      !gl.outstandingPrincipal || isNaN(parseFloat(gl.outstandingInterest))
    );
    if (hasEmptyGL) { showMessage('Enter outstanding amounts for all selected GLs', 'error'); return; }

    const action = formMode === 'create' ? 'Create' : 'Save';

    setConfirmDialog({
      open: true,
      title: `${action} Closing Transaction`,
      message: formMode === 'create'
        ? 'Are you sure you want to create this closing transaction as Draft?'
        : 'Are you sure you want to save changes to this closing transaction?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        const combinedRatio = selectedGLs.reduce((s, gl) => s + gl.allocationRatio, 0);
        const addonP = parseFloat(formData.addonOutstandingPrincipal) || 0;
        const addonI = parseFloat(formData.addonOutstandingInterest) || 0;

        try {
          if (formMode === 'create') {
            await createClosing({
                mappingId: formData.mappingId,
                addonOutstandingPrincipal: addonP,
                addonOutstandingInterest: addonI,
                glEntries: selectedGLs.map((gl) => ({
                  mappedGlId: gl.mappedGlId,
                  outstandingPrincipal: parseFloat(gl.outstandingPrincipal) || 0,
                  outstandingInterest: parseFloat(gl.outstandingInterest) || 0,
                  tokenLossCharge: parseFloat(gl.tokenLossCharge) || 0,
                })),
              } as any);
              await refetch();

            showMessage('Closing transaction created as Draft', 'success');
          } else if (selectedRecord) {
            await updateClosing(selectedRecord.id, {
                addonOutstandingPrincipal: addonP,
                addonOutstandingInterest: addonI,
                rowVersion: selectedRecord.rowVersion ?? 0,
                glEntries: selectedGLs.map((gl) => ({
                  mappedGlId: gl.mappedGlId,
                  outstandingPrincipal: parseFloat(gl.outstandingPrincipal) || 0,
                  outstandingInterest: parseFloat(gl.outstandingInterest) || 0,
                  tokenLossCharge: parseFloat(gl.tokenLossCharge) || 0,
                })),
              });
              await refetch();

            showMessage('Closing transaction updated successfully', 'success');
          }
          setFormModalOpen(false);
        } catch (err: any) {
          const msg = err?.response?.data?.message || 'Failed to save closing transaction';
          showMessage(msg, 'error');
        }
      },
    });
  }, [formMode, formData, closings.length, selectedRecord, showMessage, createClosing, updateClosing, refetch]);

  const handleDelete = useCallback((record: ClosingRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Closing Transaction',
      message: `Are you sure you want to delete closing ${record.closingRef}?`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await deleteClosing(record.id);
            await refetch();

          showMessage('Closing transaction deleted successfully', 'success');
        } catch (err: any) {
          const msg = err?.response?.data?.message || 'Failed to delete closing transaction';
          showMessage(msg, 'error');
        }
      },
    });
  }, [showMessage, deleteClosing, refetch]);

  const handleSubmit = useCallback((record: ClosingRecord) => {
    const totalSettlement = (record.glEntries ?? []).reduce((s, gl) => s + gl.outstandingPrincipal + gl.outstandingInterest, 0)
      + record.addonSettlementPrincipal + record.addonSettlementInterest;

    setConfirmDialog({
      open: true,
      title: 'Submit for Approval',
      message: `Are you sure you want to submit closing ${record.closingRef} (Total Settlement: ${formatCurrency(totalSettlement)}) for approval?`,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await submitClosing(record.id);
            await refetch();

          showMessage('Closing transaction submitted for approval', 'success');
        } catch (err: any) {
          const msg = err?.response?.data?.message || 'Failed to submit closing transaction';
          showMessage(msg, 'error');
        }
      },
    });
  }, [showMessage, submitClosing, refetch]);

  const handleDirectSubmit = useCallback(() => {
    if (!formData.mappingId) { showMessage('Please search for a loan mapping first', 'error'); return; }
    if (!formData.addonOutstandingPrincipal || parseFloat(formData.addonOutstandingPrincipal) <= 0) { showMessage('Outstanding Principal is required', 'error'); return; }
    const oiValue2 = parseFloat(formData.addonOutstandingInterest) || 0;
    if (oiValue2 < 0) { showMessage('Outstanding Interest cannot be negative', 'error'); return; }

    const selectedGLs = formData.glEntries.filter((gl) => gl.selected);
    if (selectedGLs.length === 0) { showMessage('Select at least one Gold Loan', 'error'); return; }

    const hasEmptyGL = selectedGLs.some((gl) => !gl.outstandingPrincipal || isNaN(parseFloat(gl.outstandingInterest)));
    if (hasEmptyGL) { showMessage('Enter outstanding amounts for all selected GLs', 'error'); return; }

    setConfirmDialog({
      open: true,
      title: 'Submit Closing Transaction',
      message: 'Are you sure you want to submit this closing transaction directly for approval?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        const combinedRatio = selectedGLs.reduce((s, gl) => s + gl.allocationRatio, 0);
        const addonP = parseFloat(formData.addonOutstandingPrincipal) || 0;
        const addonI = parseFloat(formData.addonOutstandingInterest) || 0;

        try {
          let recordId: string;

          if (formMode === 'edit' && selectedRecord) {
            // Update existing, then submit
            await updateClosing(selectedRecord.id, {
              addonOutstandingPrincipal: addonP,
              addonOutstandingInterest: addonI,
              rowVersion: selectedRecord.rowVersion ?? 0,
              glEntries: selectedGLs.map((gl) => ({
                mappedGlId: gl.mappedGlId,
                outstandingPrincipal: parseFloat(gl.outstandingPrincipal) || 0,
                outstandingInterest: parseFloat(gl.outstandingInterest) || 0,
                tokenLossCharge: parseFloat(gl.tokenLossCharge) || 0,
              })),
            });
            recordId = selectedRecord.id;
          } else {
            // Create new, then submit
            const created = await createClosing({
              mappingId: formData.mappingId,
              addonOutstandingPrincipal: addonP,
              addonOutstandingInterest: addonI,
              glEntries: selectedGLs.map((gl) => ({
                mappedGlId: gl.mappedGlId,
                outstandingPrincipal: parseFloat(gl.outstandingPrincipal) || 0,
                outstandingInterest: parseFloat(gl.outstandingInterest) || 0,
                tokenLossCharge: parseFloat(gl.tokenLossCharge) || 0,
              })),
            });
            recordId = String(created?.id);
          }

          if (recordId) await submitClosing(recordId);
          await refetch();

          showMessage('Closing transaction submitted for approval', 'success');
          setFormModalOpen(false);
        } catch (err: any) {
          const msg = err?.response?.data?.message || 'Failed to submit closing transaction';
          showMessage(msg, 'error');
        }
      },
    });
  }, [formMode, formData, closings.length, selectedRecord, showMessage, createClosing, updateClosing, submitClosing, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: ClosingRecord): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      {
        id: 'view',
        label: 'View Details',
        icon: <IconSystem.eye size={18} />,
        onClick: () => handleView(row),
      },
    ];

    if (row.statusCode === 'DRAFT' || row.statusCode === 'RETURNED') {
      actions.push({
        id: 'edit',
        label: 'Edit',
        icon: <IconSystem.edit size={18} />,
        onClick: () => handleEdit(row),
      });
    }

    if (row.statusCode === 'DRAFT' || row.statusCode === 'RETURNED') {
      actions.push({
        id: 'submit',
        label: row.statusCode === 'RETURNED' ? 'Resubmit for Approval' : 'Submit for Approval',
        icon: <IconSystem.check size={18} />,
        onClick: () => handleSubmit(row),
      });
    }

    if (row.statusCode === 'DRAFT') {
      actions.push({
        id: 'delete',
        label: 'Delete',
        icon: <IconSystem.trash size={18} />,
        onClick: () => handleDelete(row),
        variant: 'danger' as const,
      });
    }

    return actions;
  }, [handleView, handleEdit, handleSubmit, handleDelete]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<ClosingRecord>[] = useMemo(() => [
    { id: 'closingRef', header: 'Closing Ref', accessorKey: 'closingRef', size: 150 },
    { id: 'mappingRef', header: 'Mapping Ref', accessorKey: 'mappingRef', size: 150 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 140 },
    { id: 'businessUnitName', header: 'Branch', accessorKey: 'businessUnitName', size: 140, cell: ({ getValue }: any) => getValue() || '-' },
    {
      id: 'addonSettlement',
      header: 'Add-on Settlement',
      accessorKey: 'addonSettlementPrincipal',
      size: 150,
      cell: ({ row }: any) => {
        const r = row.original;
        return <span style={{ fontWeight: 500 }}>{formatCurrency(r.addonSettlementPrincipal + r.addonSettlementInterest)}</span>;
      },
    },
    {
      id: 'glSettlement',
      header: 'GL Settlement',
      accessorKey: 'glSettlement',
      size: 140,
      cell: ({ getValue }: any) => (
        <span style={{ fontWeight: 500 }}>{formatCurrency(getValue() ?? 0)}</span>
      ),
    },
    {
      id: 'totalSettlement',
      header: 'Total Settlement P&I',
      accessorKey: 'addonSettlementPrincipal',
      size: 170,
      cell: ({ row }: any) => {
        const r = row.original;
        const total = r.addonSettlementPrincipal + r.addonSettlementInterest + (r.glSettlement ?? 0);
        return <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{formatCurrency(total)}</span>;
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 150,
      cell: ({ getValue }: any) => {
        const status = getValue();
        return (
          <span style={getStatusBadgeStyle(status ?? '')}>
            {(status ?? '').replace(/_/g, ' ')}
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'New Closing',
      onClick: handleAddNew,
      tooltip: 'Create New Closing Transaction',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: () => showMessage('Export functionality will be available after API integration', 'info'),
      tooltip: 'Export',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Closing Transactions"
        icon={<IconSystem.xCircle size={20} />}
        description="Manage GL closing transactions with proportionate settlement."
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search closings...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && closings.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load closing transactions</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && closings.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              enableColumnFilters={false}
              rowActions={getRowActions}
              onSelectionChange={setSelectedRows}
              emptyMessage="No closing transactions found"
            />
          </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'RETURNED', label: 'Returned' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); }}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      <ClosingFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        mappingOptions={mappingOptions}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onSubmit={handleDirectSubmit}
        onClose={() => setFormModalOpen(false)}
      />

      {/* View Modal */}
      <ClosingViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default ClosingTransactionScreen;
